/**
 * Página de Contacto - Estilo Awwwards Interior
 * Información de contacto con diseño elegante y funcional
 */

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection } from '../components/animations';
import ContactInfo from '../components/contact/ContactInfo';

gsap.registerPlugin(ScrollTrigger);

/**
 * Página de contacto con estilo Awwwards
 * @returns {JSX.Element} Página de contacto inmersiva
 */
const ContactPage = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* Hero Section - Estilo Awwwards */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo negro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de contacto */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'url(/images/CONTACTO/CONTACTO.webp)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                Contacto
              </span>
            </AnimatedSection>

            {/* Título */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              Contacta con{' '}
              <span className="text-intercasa-yellow">Nosotros</span>
            </h1>

            {/* Subtítulo */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              Estamos aquí para ayudarte. Ponte en contacto con nuestro equipo.
            </p>
          </div>
        </div>
      </section>

      {/* Información de contacto */}
      <section className="bg-white py-24 md:py-32">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-5xl mx-auto">
            <ContactInfo />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-intercasa-red py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                Seguimos trabajando cada día para ofrecerte 
                <span className="block text-intercasa-yellow">calidad y sabor</span>
              </h2>
            </AnimatedSection>
            
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="text-white/80 text-lg leading-relaxed mb-10">
                Tu opinión es importante para nosotros. Nos ayuda a mejorar y seguir creciendo como empresa familiar canaria.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+34928732001"
                  className="inline-flex items-center justify-center bg-white text-intercasa-red font-lato font-semibold px-8 py-4 rounded-lg hover:bg-intercasa-yellow hover:text-intercasa-dark transition-colors duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  Llamar ahora
                </a>
                
                <a 
                  href="mailto:info@intercasacanarias.es"
                  className="inline-flex items-center justify-center bg-intercasa-dark text-white font-lato font-semibold px-8 py-4 rounded-lg hover:bg-intercasa-dark/90 transition-colors duration-300"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Enviar email
                </a>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </ImmersiveLayout>
  );
};

export default ContactPage;